import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const http = axios.create({ baseURL: API });

http.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("hgs_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

/** Turn any URL stored in the DB into a browser-usable media URL. */
export function mediaUrl(url) {
  if (!url) return url;
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url; // e.g. legacy /manus-storage paths (not transferable)
}

const get = (u, params) => http.get(u, { params }).then((r) => r.data);
const post = (u, body) => http.post(u, body).then((r) => r.data);
const put = (u, body) => http.put(u, body).then((r) => r.data);
const del = (u, params) => http.delete(u, { params }).then((r) => r.data);

export const api = {
  auth: {
    login: (email, password) => post("/auth/login", { email, password }),
    me: () => get("/auth/me"),
    logout: () => post("/auth/logout"),
  },
  dashboard: { stats: () => get("/dashboard/stats") },
  projects: {
    list: (params) => get("/projects", params),
    create: (b) => post("/projects", b),
    update: (id, b) => put(`/projects/${id}`, b),
    archive: (id) => post(`/projects/${id}/archive`),
    remove: (id) => del(`/projects/${id}`),
  },
  cameras: {
    list: (params) => get("/cameras", params),
    create: (b) => post("/cameras", b),
    update: (id, b) => put(`/cameras/${id}`, b),
    remove: (id) => del(`/cameras/${id}`),
  },
  clips: {
    list: (params) => get("/clips", params),
    remove: (id) => del(`/clips/${id}`),
    rerunAi: (id) => post(`/clips/${id}/rerun-ai`),
    purgeUnavailable: (b) => post("/clips/purge-unavailable", b),
  },
  films: {
    list: (params) => get("/films", params),
    calendar: (params) => get("/films/calendar", params),
    events: (id) => get(`/films/${id}/events`),
    generateRange: (b) => post("/films/generate-range", b),
    regenerate: (b) => post("/films/regenerate", b),
    bulkRegenerate: (b) => post("/films/bulk-regenerate", b),
    concatenate: (id) => post(`/films/${id}/concatenate`),
    remove: (id) => del(`/films/${id}`),
  },
  events: { list: (params) => get("/events", params), remove: (id) => del(`/events/${id}`) },
  users: {
    list: () => get("/users"),
    create: (b) => post("/users", b),
    setRole: (id, role) => put(`/users/${id}/role`, { role }),
    setPermissions: (id, perms) => put(`/users/${id}/permissions`, perms),
    remove: (id) => del(`/users/${id}`),
  },
  members: {
    list: (projectId) => get("/project-members", { projectId }),
    add: (b) => post("/project-members", b),
    remove: (projectId, userId) => post("/project-members/remove", { projectId, userId }),
  },
  settings: { get: () => get("/settings"), update: (b) => put("/settings", b) },
  gateway: {
    config: () => get("/gateway/config"),
    streamUrl: (cameraId) => get("/gateway/stream-url", { cameraId }),
    test: () => get("/gateway/test"),
  },
  nas: {
    status: () => get("/nas/status"),
    browse: (path) => get("/nas/browse", path ? { path } : undefined),
    import: (b) => post("/nas/import", b),
    downloadBlob: (path) => http.get("/nas/download", { params: { path }, responseType: "blob" }).then((r) => r.data),
  },
  cloudSync: {
    getConfig: () => get("/cloud-sync/config"),
    updateConfig: (b) => put("/cloud-sync/config", b),
    listMappings: () => get("/cloud-sync/mappings"),
    createMapping: (b) => post("/cloud-sync/mappings", b),
    autoMap: () => post("/cloud-sync/auto-map"),
    repairCameras: () => post("/cloud-sync/repair-cameras"),
    updateMapping: (id, b) => put(`/cloud-sync/mappings/${id}`, b),
    deleteMapping: (id) => del(`/cloud-sync/mappings/${id}`),
    listLogs: (limit = 20) => get("/cloud-sync/logs", { limit }),
    testNas: (b) => post("/cloud-sync/test-nas", b),
    testReolink: (b) => post("/cloud-sync/test-reolink", b),
    listAccounts: () => get("/cloud-sync/accounts"),
    createAccount: (b) => post("/cloud-sync/accounts", b),
    updateAccount: (id, b) => put(`/cloud-sync/accounts/${id}`, b),
    deleteAccount: (id) => del(`/cloud-sync/accounts/${id}`),
    loginAccount: (id) => post(`/cloud-sync/accounts/${id}/login`),
    verifyAccount: (id, code) => post(`/cloud-sync/accounts/${id}/verify`, { code }),
    resendAccount: (id) => post(`/cloud-sync/accounts/${id}/resend`),
    agentScript: () => http.get("/cloud-sync/agent-script", { responseType: "text" }).then((r) => r.data),
    trigger: () => post("/cloud-sync/trigger"),
    enableAuto: (b) => post("/cloud-sync/enable-auto", b),
    disableAuto: () => post("/cloud-sync/disable-auto"),
    backfill: (b) => post("/cloud-sync/backfill", b),
    backfillCamera: (b) => post("/cloud-sync/backfill-camera", b),
    backfillCancel: () => post("/cloud-sync/backfill-cancel"),
    retryNas: () => post("/cloud-sync/retry-nas"),
  },
};

export function apiError(e) {
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message || "Eroare necunoscută";
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return JSON.stringify(d);
}
